import { TreeNodeData, XY } from './model';

export type NodeStat = {
  node: Omit<TreeNodeData, 'children'>;
  count: number;
  depth: number;
  maxDepth: number;
  maxHeight: number;
  expandedHeight: number;
  index: number;
  indexInParent: number;
  finalChildInParent: boolean;
  expandedIndex: number | null;
};

export function calculateNodeStatistics(
  tree_nodes: TreeNodeData[],
  expansionSet: Set<number>,
) {
  const node_stats = new Map<number, NodeStat>();
  let max_depth = 1;
  let index = 0;
  let expandedIndex = 0;

  function dfs(
    node: TreeNodeData,
    depth: number,
    indexInParent: number,
    finalChild: boolean,
    parentExpanded: boolean,
  ) {
    let max_child_depth = 0;
    let count = 0;
    let max_height = 1;
    let expanded_height = 1;
    let position_index = index++;
    let expanded_index = parentExpanded ? expandedIndex++ : null;

    let node_is_expanded = expansionSet.has(node.id);

    if (Array.isArray(node.children)) {
      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i];
        const {
          count: child_count,
          maxDepth: child_depth,
          maxHeight: child_max_height,
          expandedHeight: child_expanded_height,
        } = dfs(
          child,
          depth + 1,
          i,
          i === node.children.length - 1,
          node_is_expanded && parentExpanded,
        );

        max_height += child_max_height;

        if (node_is_expanded) {
          expanded_height += child_expanded_height;
        }

        count += child_count + 1;
        max_child_depth = Math.max(max_child_depth, child_depth);
      }
    }

    const resulting_depth = Math.max(depth, max_child_depth);
    max_depth = Math.max(max_depth, resulting_depth);

    const r = {
      count,
      depth,
      index: position_index,
      indexInParent,
      finalChildInParent: finalChild,
      maxDepth: resulting_depth,
      maxHeight: max_height,
      expandedIndex: expanded_index,
      expandedHeight: expanded_height,
      node: {
        id: node.id,
        text: node.text,
      },
    };

    node_stats.set(node.id, r);

    return r;
  }

  tree_nodes.forEach((n, i) => dfs(n, 1, i, i === tree_nodes.length - 1, true));

  return {
    node_stats,
    max_depth,
    max_height: tree_nodes.reduce(
      (acc, val) => acc + (node_stats.get(val.id)?.maxHeight ?? 0),
      0,
    ),
  };
}

export function getNodePath(element: HTMLElement) {
  let nodePath = [Number(element.dataset.index)];

  let parentNode = element.parentElement;

  while (parentNode?.dataset.id !== 'root') {
    let parentNodeIndex = parentNode?.dataset.index;

    if (parentNodeIndex) {
      nodePath.unshift(Number(parentNodeIndex));
    }

    if (!parentNode?.parentElement) {
      throw new Error('Invalid parent for node');
    }

    parentNode = parentNode.parentElement;
  }

  return nodePath;
}

export function getDragTargetPath(
  targetElement: HTMLElement,
  targetContentElement: HTMLElement,
  dragElement: HTMLElement,
  dragPosition: XY,
) {
  const targetBoundingRect = targetContentElement.getBoundingClientRect();
  const targetElementRect = targetElement.getBoundingClientRect();
  const dragBoundingRect = dragElement.getBoundingClientRect();
  const targetPath = getNodePath(targetElement);

  if (
    dragPosition.y + dragBoundingRect.height >
    targetElementRect.y + targetElementRect.height
  ) {
    return [
      ...targetPath.slice(0, targetPath.length - 1),
      targetPath[targetPath.length - 1] + 1,
    ];
  } else if (dragPosition.y < targetBoundingRect.y) {
    return targetPath;
  } else {
    return [...targetPath, 0];
  }
}

export function getNodeParentFromPath(
  root: TreeNodeData,
  path: number[],
): TreeNodeData & { children: TreeNodeData[] } {
  let parent = root;

  for (let i = 0; i < path.length - 1; i++) {
    if (!parent.children) {
      throw new Error('Missing expected children while finding parent path');
    }

    parent = parent.children[path[i]];
  }

  if (!parent.children) {
    throw new Error('Found parent but is has no children');
  }

  return parent as TreeNodeData & { children: TreeNodeData[] };
}

export function moveTreeElement(
  tree: TreeNodeData[],
  fromIndex: number[],
  toIndex: number[],
): TreeNodeData[] {
  const root = { id: -1, text: '', children: tree } as TreeNodeData;
  const sourceParent = getNodeParentFromPath(root, fromIndex);
  const sourceElement = sourceParent.children[fromIndex[fromIndex.length - 1]];

  if (!sourceElement) {
    throw new Error('Unable to find source element in tree');
  }

  const targetParent = getNodeParentFromPath(root, toIndex);

  if (sourceParent === targetParent) {
    sourceParent.children.splice(fromIndex[fromIndex.length - 1], 1);
    targetParent.children.splice(toIndex[toIndex.length - 1], 0, sourceElement);
  } else {
    targetParent.children.splice(toIndex[toIndex.length - 1], 0, sourceElement);
    sourceParent.children.splice(fromIndex[fromIndex.length - 1], 1);
  }

  return tree;
}
